// Maps font size to how wide the characters are at that font size in pixels. Measured, not declarative.
const _widths: {[key: number]: number} = {
  3: 2,
  4: 2,
  5: 3,
  6: 3,
  7: 4,
  8: 4,
  9: 5,
  10: 5,
  11: 6,
  12: 6,
  13: 7,
  14: 7,
  15: 8,
  16: 8,
  17: 9,
  18: 9,
  19: 10,
  20: 10,
  21: 11,
  22: 11,
  23: 12,
  24: 12,
};

// Maps font size to how tall the characters are at that font size in pixels. Measured, not declarative.
const _heights: {[key: number]: number} = {
  3: 4,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 9,
  9: 10,
  10: 11,
  11: 11,
  12: 12,
  13: 13,
  14: 15,
  15: 16,
  16: 17,
  17: 18,
  18: 18,
  19: 20,
  20: 21,
  21: 22,
  22: 23,
  23: 24,
  24: 26,
};

class Type {
  fontSize: number;
  letterSpacing: number;
  _height: number;
  _width: number;
  widths: {[key: number]: number};
  heights: {[key: number]: number};
  /**
   * Whether the type fits in one of our bucketed pixel widths. If it is too large or too small, `manual` will
   * be set to true.
   */
  manual: boolean;
  verticalOffset!: number;
  static smallest: Type;
  static largest: Type;
  static widths: {[key: number]: number};
  static heights: {[key: number]: number};

  static initClass(): void {
    this.prototype.verticalOffset = 4; // text seems to render 4px lower than the expected baseline.
    this.widths = _widths;
    this.heights = _heights;
    this.smallest = new Type(3, 0);
    this.largest = new Type(24, 48);
  }

  static fitToWidth(chars: number, width: number): number | null {
    for (let i = 24; i >= 3; i--) {
      if (_widths[i] * chars <= width) {
        return i;
      }
    }
    return null;
  }

  static typeToFitWidth(chars: number, width: number): Type {
    if (chars < 2) {
      throw new Error(`Cannot fit ${chars} characters in width ${width}`);
    }
    const targetFontSize = Type.fitToWidth(chars, width);
    const targetLetterSpacing = Math.floor((width - chars * _widths[targetFontSize!]) / (chars - 1));
    const type = new Type(targetFontSize, targetLetterSpacing);
    if (targetFontSize == null) {
      type.setWidth(width / chars);
    }
    return type;
  }

  typeToFitWidth(chars: number, width: number): void {
    const targetFontSize = Type.fitToWidth(chars, width);
    const targetLetterSpacing = Math.floor((width - chars * _widths[targetFontSize!]) / (chars - 1));
    if (targetFontSize == null) {
      this.setWidth(width / chars);
    } else {
      this.setFontSize(targetFontSize);
      this.setLetterSpacing(targetLetterSpacing);
    }
  }

  constructor(fontSize: number | null, letterSpacing: number = 0) {
    if (fontSize == null) {
      fontSize = 15;
    }
    this.fontSize = fontSize;
    this.letterSpacing = letterSpacing;
    this._height = _heights[this.fontSize];
    this._width = _widths[this.fontSize];
    this.widths = _widths;
    this.heights = _heights;
    this.manual = false;
  }

  copy(type: Type): void {
    this._width = type._width;
    this._height = type._height;
    this.manual = type.manual;
    this.fontSize = type.fontSize;
    this.letterSpacing = type.letterSpacing;
  }

  // Returns whether or not we should render the font.
  fitToWidth(chars: number, width: number, param?: {allowFractionalSpacing?: boolean}): boolean {
    if (param == null) {
      param = {};
    }
    const {allowFractionalSpacing} = param;
    const targetFontSize = Type.fitToWidth(chars, width);
    if (targetFontSize == null) {
      this.setWidth(width / chars);
      return false;
    }
    let targetLetterSpacing = (width - chars * _widths[targetFontSize]) / (chars - 1);
    if (!allowFractionalSpacing) {
      targetLetterSpacing = Math.floor(targetLetterSpacing);
    }
    this.setFontSize(targetFontSize);
    this.setLetterSpacing(targetLetterSpacing);
    return true;
  }

  setFontSize(fontSize: number): void {
    this._height = _heights[fontSize];
    this._width = _widths[fontSize];
    this.fontSize = fontSize;
    this.manual = false;
  }

  setWidth(width: number): void {
    this.manual = true;
    this.setLetterSpacing(0);
    this._width = width;
    this._width = width;
  }

  setHeight(height: number): void {
    this.manual = true;
    this._height = height;
  }

  setLetterSpacing(letterSpacing: number): void {
    this.letterSpacing = letterSpacing;
  }

  height(): number {
    return this._height;
  }

  charWidth(): number {
    return this._width + this.letterSpacing;
  }

  numChars(width: number): number {
    return Math.floor((width + this.letterSpacing) / this.charWidth());
  }

  width(numChars: number): number {
    return numChars * this.charWidth() - this.letterSpacing;
  }
}
Type.initClass();

export default Type;
